import { Suspense, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { CarouselLayout, mapCarouselLayoutSettings } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import {
  getImagesByQuality,
  getQuality,
  getSecondColor
} from '../../../helpers';
import { useResizeWidth } from '../../../utils';
import { useLightbox } from '../../lightbox';
import { MIN_CAROUSEL_HEIGHT, renderItem, renderLoader } from '../utils';

export function Carousel({ images, reportVisibleImages }) {
  const { showLightbox } = useLightbox();

  const settings = useSettings();
  const {
    carouselAutoplay,
    carouselImageRatio,
    backgroundType,
    backgroundColor,
    backgroundImageOverlayColor,
    displayDeactivation
  } = settings;
  const carouselSettings = mapCarouselLayoutSettings(settings);
  const { measureRef, width } = useResizeWidth();
  const quality = getQuality(width);
  const currentImages = useMemo(
    () => getImagesByQuality(images, quality),
    [images, quality]
  );

  useEffect(() => {
    reportVisibleImages(images.map(({ id }) => id));
  }, [images, reportVisibleImages]);

  const secondColor = getSecondColor({
    backgroundType,
    backgroundColor,
    backgroundImageOverlayColor
  });

  const autoplay = carouselAutoplay && !showLightbox && !displayDeactivation;

  return (
    <div ref={measureRef}>
      <Suspense fallback={null}>
        <StyledCarousel
          {...carouselSettings}
          loop
          reportSlideChangeInstantly={
            carouselSettings.swipeDesktopEnable ||
            carouselSettings.swipeMobileEnable
          }
          autoplay={autoplay}
          dataSource={currentImages}
          renderItem={(item, itemIndex) =>
            renderItem(item, itemIndex, carouselImageRatio)
          }
          renderLoader={renderLoader({ color: secondColor })}
          _minHeight={MIN_CAROUSEL_HEIGHT}
          _heightNormalization={!!carouselImageRatio}
        />
      </Suspense>
    </div>
  );
}

const StyledCarousel = styled(CarouselLayout)`
  & .swiper-wrapper {
    align-items: center !important;
    min-height: ${({ _minHeight }) => _minHeight}px;

    ${({ _heightNormalization }) =>
      _heightNormalization &&
      css`
        height: 100% !important;
        align-items: stretch !important;

        & .es-carousel-layout-item {
          height: 100% !important;
        }
      `}
  }
`;
